import { ChartBarHorizontal, CurrencyEur, PencilSimple, Ruler, TrendUp } from '@phosphor-icons/react';
import { ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand } from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { mietentwicklung } from '../../../../../lib/immobilien/einheitDetail';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import { useEinheitSeite } from '../EinheitSeitenKontext';

/**
 * „Gesamtmiete & Entwicklung“ und „Mietzusammensetzung“: heute gültiger Mietstand und die Mietstände
 * aus miet_historie. Neue Mietstände legt der bestehende Mietverhältnis-Dialog an.
 */
export function MieteBereich({ fokus }: { fokus: 'entwicklung' | 'zusammensetzung' }) {
  const s = useEinheitSeite();
  const { miete, aktuell } = s;
  if (!aktuell) {
    return <LeerZustand icon={CurrencyEur} titel="Keine Miete" text="Die Einheit steht leer – es gibt kein laufendes Mietverhältnis." />;
  }
  const teile = [
    { label: 'Kaltmiete', wert: miete.kalt, farbe: 'var(--acc)' },
    { label: 'Nebenkosten', wert: miete.nk, farbe: 'var(--info)' },
    { label: 'Heizkosten', wert: miete.hk, farbe: 'var(--bad)' },
    { label: 'Weitere Kosten', wert: miete.weitere, farbe: 'var(--warn)' },
  ];
  const proQm = s.flaeche > 0 && miete.kalt > 0 ? Math.round((miete.kalt / s.flaeche) * 100) / 100 : null;

  return (
    <>
      <KpiReihe>
        <KpiTile label="Gesamtmiete" wert={eur(miete.gesamt)} sub="pro Monat" icon={CurrencyEur} ton="acc" />
        <KpiTile label="Kaltmiete" wert={eur(miete.kalt)} sub={proQm !== null ? `${eur(proQm)} / m²` : 'ohne Fläche'} icon={Ruler} />
        <KpiTile label="Vorauszahlungen" wert={eur(miete.nk + miete.hk)} sub={`NK ${eur(miete.nk)} · HK ${eur(miete.hk)}`} icon={ChartBarHorizontal} />
      </KpiReihe>

      <InnenKarte>
        <KartenTitel icon={ChartBarHorizontal} rechts={eur(miete.gesamt)}>Zusammensetzung</KartenTitel>
        {miete.gesamt > 0 && (
          <div className="flex overflow-hidden" style={{ height: 16, borderRadius: 8, gap: 3 }} aria-hidden>
            {teile.filter(t => t.wert > 0).map(t => <span key={t.label} style={{ flex: t.wert, background: t.farbe, borderRadius: 8 }} />)}
          </div>
        )}
        <div className="flex flex-col">
          {teile.map(t => (
            <div key={t.label} className="flex items-center justify-between" style={{ padding: '10px 0', borderBottom: '1px solid var(--line)', gap: 10 }}>
              <span className="flex items-center" style={{ gap: 8, fontSize: 14, color: 'var(--ink2)' }}>
                <span aria-hidden style={{ width: 10, height: 10, borderRadius: 3, background: t.farbe }} />{t.label}
              </span>
              <span className="immo-zahl" style={{ fontSize: 14, fontWeight: 800 }}>{eur(t.wert)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between" style={{ padding: '12px 0 0', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>Gesamtmiete</span>
            <span className="immo-zahl" style={{ fontSize: 16, fontWeight: 800 }}>{eur(miete.gesamt)}</span>
          </div>
        </div>
        {s.laufende.length > 1 && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Summe aus {s.laufende.length} laufenden Mietverhältnissen.</span>}
      </InnenKarte>

      {s.laufende.map(m => {
        const staende = mietentwicklung(m.miet_historie);
        return (
          <InnenKarte key={m.id}>
            <KartenTitel icon={TrendUp} rechts={s.laufende.length > 1 ? kontaktName(m) : undefined}>Entwicklung</KartenTitel>
            {staende.length === 0 ? (
              <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Mietstände hinterlegt – die Miete steht direkt im Mietverhältnis.</span>
            ) : (
              <div className="flex flex-col">
                {[...staende].reverse().map((st, i, liste) => {
                  const vorher = liste[i + 1];
                  const diff = vorher ? Math.round((st.gesamt - vorher.gesamt) * 100) / 100 : null;
                  const zukunft = st.ab > s.heute;
                  return (
                    <div key={`${st.ab}-${i}`} className="flex items-center justify-between flex-wrap" style={{ padding: '10px 0', borderBottom: '1px solid var(--line)', gap: 10 }}>
                      <span className="flex flex-col">
                        <span className="immo-zahl" style={{ fontSize: 14, fontWeight: 700 }}>ab {deDatum(st.ab)}{zukunft ? ' · geplant' : ''}</span>
                        <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>Kalt {eur(st.kalt)}</span>
                      </span>
                      <span className="flex items-center" style={{ gap: 10 }}>
                        {diff !== null && diff !== 0 && <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700, color: diff > 0 ? 'var(--ok)' : 'var(--bad)' }}>{eur(diff, { vorzeichen: true })}</span>}
                        <span className="immo-zahl" style={{ fontSize: 15, fontWeight: 800 }}>{eur(st.gesamt)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <ImmoButton icon={PencilSimple} onClick={() => s.bearbeiteMietverhaeltnis(m, 'miete')} style={{ alignSelf: 'flex-start' }}>
              {fokus === 'entwicklung' ? 'Mietstand ändern' : 'Miete bearbeiten'}
            </ImmoButton>
          </InnenKarte>
        );
      })}
    </>
  );
}
